/**
 * @file src/offline/offlineMode.js
 * @description Whether this session is running on stored bytes, and what that forbids.
 *
 * An offline boot is not "the network happens to be down". It is a session that started from a
 * manifest on this disk, with a session cookie nobody can verify, against a backend that was never
 * asked whether this person may still do any of it. Reads that fail are a nuisance; WRITES made on
 * that footing are a lie told to the server in the person's name — a layout saved from a session
 * whose permissions were never checked, an event reporting a play the platform has no other record
 * of, a setting written over one that may have changed elsewhere.
 *
 * So the mode is declared once, at boot, and enforced at ONE door: `fetchJsonFromApi`. Not as a
 * branch in each of the thirteen modules that call it — those branches are where a write eventually
 * slips through, because the person adding the fourteenth caller has no reason to know this rule
 * exists. The door knows.
 *
 * ## Why writes are refused rather than queued
 *
 * A queued write is a promise to replay it, and replaying is where this gets genuinely wrong: the
 * layout in the queue was arranged against a roster that may since have lost an item, and the
 * session it reports may be hours old by the time a connection returns. Refusing is honest and
 * costs one thing — a rearrangement made offline is session-local, which is exactly what it already
 * is for a viewer without edit permission.
 *
 * ## What "offline" is NOT
 *
 * `navigator.onLine` is not consulted here. It answers "is there a link", which is famously not the
 * same question, and a captive portal or a dead backend answers `true`. The mode is set by the boot
 * that chose to run from the manifest, because that boot is the only thing that actually knows.
 */

/** One synthetic status for every refused write, so a caller can recognise it. */
export const OFFLINE_WRITE_STATUS = 503;

/** The body a refused write answers with — the callers all read `body.code`. */
export const OFFLINE_WRITE_CODE = 'offline-read-only';

let readOnly = false;

/**
 * Declare that this session is running from stored bytes.
 *
 * Called once by the boot that read a manifest instead of fetching a roster. There is no way back
 * within a session on purpose: the app was composed from pinned releases, and a connection
 * returning mid-session does not retroactively make those the live ones. The way back is a reload.
 */
export function enterOfflineReadOnly() {
  readOnly = true;
}

/** Whether writes are refused. */
export function isOfflineReadOnly() {
  return readOnly;
}

/** Test-only: put the module back to a session that has not booted offline. */
export function resetOfflineModeForTests() {
  readOnly = false;
}

/** Every method that only asks a question. Anything else changes something on the server. */
const READ_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/** Whether a request would write. */
export function isWriteMethod(method) {
  return !READ_METHODS.has(String(method || 'GET').toUpperCase());
}

/**
 * The answer a refused write gets: a real `Response`, not a thrown error.
 *
 * Every caller of `fetchJsonFromApi` already handles a non-ok response — they read `response.ok`,
 * parse a body for a code, and say something. Throwing instead would take a path most of them do
 * not have, and the ones that do treat a throw as "the app is broken" rather than "that did not
 * save". A 503 with a code is the shape they are all already written for.
 */
export function buildOfflineWriteResponse() {
  return new Response(JSON.stringify({ code: OFFLINE_WRITE_CODE, message: 'Offline: writes are disabled.' }), {
    status: OFFLINE_WRITE_STATUS,
    statusText: 'Offline',
    headers: { 'Content-Type': 'application/json' },
  });
}
